import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api from '../api';

const MESES_FULL = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_SHORT = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function fmtCLP(val) {
  if (!val && val !== 0) return '—';
  return '$' + Number(val).toLocaleString('es-CL');
}
function parseMonto(v) {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  return parseInt(String(v).replace(/\./g, '').replace(/[^0-9-]/g, ''), 10) || 0;
}
function parseDocs(v) {
  if (typeof v === 'number') return v;
  return parseInt(v, 10) || 0;
}

function EstadoBadge({ estado, ok, sinDatos }) {
  if (estado === 'en_cola') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">⏳ En Cola</span>;
  if (estado === 'en_progreso') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">⟳ Procesando</span>;
  if (sinDatos) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Sin datos</span>;
  if (ok === true)  return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">✓ OK</span>;
  if (ok === false) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">✗ Error</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">—</span>;
}

function TablaResumenSII({ resumenSii }) {
  if (!resumenSii || resumenSii.length === 0) return <p className="text-xs text-gray-400 italic py-3 text-center">Sin datos de resumen SII</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-xs whitespace-nowrap">
        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide font-semibold border-b">
          <tr>
            <th className="px-3 py-2 text-left">Tipo de Documento</th>
            <th className="px-3 py-2 text-right">Cant.</th>
            <th className="px-3 py-2 text-right">M. Exento</th>
            <th className="px-3 py-2 text-right">M. Neto</th>
            <th className="px-3 py-2 text-right">M. IVA</th>
            <th className="px-3 py-2 text-right font-bold text-gray-700">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {resumenSii.map((row, i) => {
            const esBoleta = row.tipoDocumento && (row.tipoDocumento.toUpperCase().includes('BOLETA') || row.tipoDocumento.toUpperCase().includes('COMPROBANTE PAGO'));
            return (
              <tr key={i} className={`hover:bg-gray-50 transition-colors ${esBoleta ? 'bg-indigo-50/50' : ''}`}>
                <td className="px-3 py-2 text-gray-700 font-medium">
                  {esBoleta && <span className="inline-block mr-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 align-middle" />}
                  {row.tipoDocumento}
                </td>
                <td className="px-3 py-2 text-right text-gray-600 font-mono">{row.totalDocumentos || row.cantidadDocumentos || 0}</td>
                <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoExento || '0'}</td>
                <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoNeto || '0'}</td>
                <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoIva || '0'}</td>
                <td className="px-3 py-2 text-right text-gray-800 font-semibold font-mono">{row.montoTotal || '0'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FilaMes({ mesNum, logCompras, logVentas, descargandoArchivo, onDescargar, onReejecutar }) {
  const [expandido, setExpandido] = useState(false);

  const totalCompras = (logCompras?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
  const totalVentas  = (logVentas?.resumenSii  || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
  const tieneAlgo    = logCompras || logVentas;
  const enProgreso   = logCompras?.estado === 'en_progreso' || logVentas?.estado === 'en_progreso';
  const enCola       = logCompras?.estado === 'en_cola'     || logVentas?.estado === 'en_cola';

  return (
    <div className={`border rounded-xl overflow-hidden transition-shadow ${expandido ? 'shadow-md' : 'shadow-sm'}`}>
      {/* Cabecera de la fila */}
      <button
        onClick={() => tieneAlgo && setExpandido(v => !v)}
        disabled={!tieneAlgo}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
          tieneAlgo ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default bg-gray-50/50'
        } ${enProgreso ? 'bg-yellow-50' : (enCola ? 'bg-blue-50/50' : '')}`}
      >
        {/* Mes */}
        <span className="w-32 font-semibold text-gray-800 text-sm flex-shrink-0">{MESES_FULL[mesNum]}</span>

        {/* Estado compras */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:inline">Compras</span>
          {logCompras
            ? <EstadoBadge estado={logCompras.estado} ok={logCompras.descargaSii?.sinDatos ? undefined : logCompras.subidaInputsoft?.ok} sinDatos={logCompras.descargaSii?.sinDatos} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Estado ventas */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:inline">Ventas</span>
          {logVentas
            ? <EstadoBadge estado={logVentas.estado} ok={logVentas.descargaSii?.sinDatos ? undefined : logVentas.subidaInputsoft?.ok} sinDatos={logVentas.descargaSii?.sinDatos} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Totales */}
        <div className="ml-auto flex items-center gap-6 text-right flex-shrink-0">
          {totalCompras > 0 && (
            <div>
              <div className="text-xs text-gray-400">Compras</div>
              <div className="text-sm font-semibold text-green-700">{fmtCLP(totalCompras)}</div>
            </div>
          )}
          {totalVentas > 0 && (
            <div>
              <div className="text-xs text-gray-400">Ventas</div>
              <div className="text-sm font-semibold text-blue-700">{fmtCLP(totalVentas)}</div>
            </div>
          )}
          {tieneAlgo && (
            <span className={`text-gray-400 transition-transform ${expandido ? 'rotate-180' : ''} text-xs ml-2`}>▼</span>
          )}
          {enProgreso && <span className="text-xs text-yellow-600 animate-pulse">⟳ En progreso</span>}
          {!tieneAlgo && <span className="text-xs text-gray-300 italic">Sin ejecución</span>}
        </div>
      </button>

      {/* Detalle expandido */}
      {expandido && (
        <div className="border-t bg-white px-4 py-4 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {[
              { tipo: 'compras', log: logCompras, color: 'green' },
              { tipo: 'ventas',  log: logVentas,  color: 'blue'  },
            ].map(({ tipo, log, color }) => (
              <div key={tipo}>
                <h4 className={`text-sm font-semibold capitalize mb-3 flex items-center gap-2 ${color === 'green' ? 'text-green-700' : 'text-blue-700'}`}>
                  <div className="flex items-center gap-2 flex-1">
                    <span className={`w-2 h-2 rounded-full ${color === 'green' ? 'bg-green-500' : 'bg-blue-500'}`} />
                    {tipo}
                    {log && <span className="font-normal text-gray-400 text-xs ml-1">— {new Date(log.fechaInicio).toLocaleString('es-CL')}</span>}
                  </div>
                  {(!log || (log.estado !== 'en_progreso' && log.estado !== 'en_cola')) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onReejecutar(mesNum, tipo); }}
                      className="px-2 py-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded text-[10px] uppercase font-bold tracking-wider shadow-sm transition-colors"
                    >
                      ▶ {log ? 'Re-ejecutar' : 'Ejecutar'}
                    </button>
                  )}
                </h4>

                {!log ? (
                  <p className="text-xs text-gray-400 italic">Sin registro de pipeline</p>
                ) : (
                  <div className="space-y-3">
                    {/* Etapas */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        ['descargaSii', 'SII'],
                        ['procesamientoCsv', 'CSV'],
                        ['subidaInputsoft', 'Inputsoft'],
                        ['ingresoBoletas', 'Boletas'],
                      ].map(([key, label]) => {
                        const etapa = log[key];
                        if (!etapa) return null;
                        return (
                          <span key={key} className={`text-xs px-2 py-1 rounded font-medium ${
                            etapa.ok ? 'bg-green-100 text-green-700' :
                            etapa.ok === false ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {etapa.ok ? '✓' : etapa.ok === false ? '✗' : '…'} {label}
                            {etapa.sinDatos ? ' (sin datos)' : ''}
                            {etapa.filasProcesadas != null ? ` (${etapa.filasProcesadas} f.)` : ''}
                          </span>
                        );
                      })}
                    </div>

                    {/* Tabla SII */}
                    <TablaResumenSII resumenSii={log.resumenSii} />

                    {/* Errores Inputsoft */}
                    {log.subidaInputsoft?.detallesError?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-orange-600 mb-1.5">
                          ⚠ Alertas Inputsoft ({log.subidaInputsoft.detallesError.length})
                        </h5>
                        <div className="max-h-32 overflow-y-auto border border-orange-200 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-orange-50 text-orange-700 sticky top-0">
                              <tr>
                                <th className="p-2 text-left font-medium">Tipo</th>
                                <th className="p-2 text-left font-medium">Folio</th>
                                <th className="p-2 text-left font-medium">Motivo</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-orange-100">
                              {log.subidaInputsoft.detallesError.map((e, i) => (
                                <tr key={i}>
                                  <td className="p-2 text-gray-500">{e.tipo || '—'}</td>
                                  <td className="p-2 font-mono text-gray-700">{e.folio}</td>
                                  <td className="p-2 text-orange-700">{e.motivo}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Archivos */}
                    <div className="flex flex-wrap gap-2">
                      {log.descargaSii?.archivo && (() => {
                        const fname = log.descargaSii.archivo.split(/[/\\]/).pop();
                        return (
                          <button onClick={() => onDescargar(log._id, fname)}
                            disabled={descargandoArchivo === fname}
                            className="inline-flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-50 transition-colors">
                            📄 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Original SII'}
                          </button>
                        );
                      })()}
                      {log.procesamientoCsv?.archivo && (() => {
                        const fname = log.procesamientoCsv.archivo.split(/[/\\]/).pop();
                        return (
                          <button onClick={() => onDescargar(log._id, fname)}
                            disabled={descargandoArchivo === fname}
                            className="inline-flex items-center gap-1.5 text-xs border border-blue-200 rounded-lg px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 disabled:opacity-50 transition-colors">
                            📊 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Procesado'}
                          </button>
                        );
                      })()}
                    </div>

                    {/* Log colapsable */}
                    <details className="group">
                      <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 select-none">
                        <span className="transition-transform group-open:rotate-90">▶</span> Ver log de ejecución
                      </summary>
                      <div className="mt-2 p-3 bg-gray-900 text-gray-300 rounded-lg text-xs font-mono max-h-56 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                        {log.logEntries?.join('\n') || 'Sin logs'}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardEmpresaContabilidad({ empresa, anio: anioInicial, onClose }) {
  const [anio,     setAnio]     = useState(anioInicial || new Date().getFullYear());
  const [logs,     setLogs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [descargandoArchivo, setDescargandoArchivo] = useState('');

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contabilidad/logs', { params: { clienteId: empresa._id, anio } });
      setLogs(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [empresa._id, anio]);

  const reejecutarProceso = async (mesNum, tipo) => {
    toast.info(`Iniciando ejecución de ${tipo} para ${MESES_FULL[mesNum]}...`);
    try {
      await api.post('/contabilidad/ejecutar', {
        clienteId: empresa._id, mes: mesNum, anio, tipoDocumento: tipo
      });
      toast.success('Proceso encolado exitosamente');
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo iniciar el proceso');
    }
  };

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const descargarArchivo = async (logId, fileName) => {
    if (!logId || !fileName) return;
    setDescargandoArchivo(fileName);
    try {
      const response = await api.get(
        `/contabilidad/logs/${logId}/archivos/${encodeURIComponent(fileName)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo descargar el archivo');
    } finally {
      setDescargandoArchivo('');
    }
  };

  const anioActual = new Date().getFullYear();
  const años = Array.from({ length: 6 }, (_, i) => anioActual - i);

  // Construir datos por mes
  const datosMes = Array.from({ length: 12 }, (_, i) => {
    const mesNum    = i + 1;
    const logC      = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'compras');
    const logV      = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'ventas');
    const totCompras = (logC?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const totVentas  = (logV?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const docsC      = (logC?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    const docsV      = (logV?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    return { mesNum, nombre: MESES_SHORT[mesNum], totCompras, totVentas, docsC, docsV, logC, logV };
  });

  // KPIs
  const totalVentasAnio  = datosMes.reduce((s, d) => s + d.totVentas, 0);
  const totalComprasAnio = datosMes.reduce((s, d) => s + d.totCompras, 0);
  const mesesOK   = datosMes.filter(d => d.logC?.estado === 'completado' || d.logV?.estado === 'completado').length;
  const mesesError = datosMes.filter(d => d.logC?.estado === 'error' || d.logV?.estado === 'error').length;
  const mesesEjecutados = datosMes.filter(d => d.logC || d.logV).length;

  // Grafico
  const dataGrafico = datosMes.map(d => ({
    mes: d.nombre,
    Ventas: d.totVentas,
    Compras: d.totCompras,
  }));

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto min-h-screen">
      {/* ── Cabecera ── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors flex-shrink-0">
          ← Volver
        </button>
        <span className="text-gray-300 hidden sm:inline">/</span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-800 truncate">{empresa.nombre}</h1>
          <p className="text-xs text-gray-400 font-mono">{empresa.rut}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <select value={anio} onChange={e => setAnio(Number(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
            {años.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={cargarDatos} title="Actualizar"
            className="px-3 py-1.5 border rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-colors">
            ↻
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-32 text-center text-gray-400">
          <div className="text-4xl mb-3">⟳</div>
          <p>Cargando información de {anio}...</p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Ventas Año', value: fmtCLP(totalVentasAnio), color: 'blue', icon: '↑' },
              { label: 'Total Compras Año', value: fmtCLP(totalComprasAnio), color: 'green', icon: '↓' },
              { label: 'Meses Ejecutados', value: `${mesesEjecutados} / 12`, color: 'purple', icon: '📅' },
              { label: 'Meses con Error', value: mesesError, color: mesesError > 0 ? 'red' : 'green', icon: mesesError > 0 ? '⚠' : '✓' },
            ].map((kpi, i) => (
              <div key={i} className="bg-white border rounded-xl p-4 shadow-sm">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{kpi.label}</div>
                <div className={`text-xl font-bold ${
                  kpi.color === 'blue' ? 'text-blue-700' :
                  kpi.color === 'green' ? 'text-green-700' :
                  kpi.color === 'purple' ? 'text-purple-700' :
                  kpi.color === 'red' ? 'text-red-600' : 'text-gray-800'
                }`}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* ── Gráfico de Barras ── */}
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Evolución Monetaria {anio} (CLP)</h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dataGrafico} margin={{ top: 5, right: 10, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="mes" fontSize={12} tick={{ fill: '#6b7280' }} />
                  <YAxis tickFormatter={v => '$' + (v / 1000000).toFixed(1) + 'M'} fontSize={11} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    formatter={(val) => fmtCLP(val)}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Ventas"  fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Compras" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Tabla de Meses ── */}
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Detalle por Mes</h2>
            <div className="space-y-2">
              {datosMes.map(d => (
                <FilaMes
                  key={d.mesNum}
                  mesNum={d.mesNum}
                  logCompras={d.logC}
                  logVentas={d.logV}
                  descargandoArchivo={descargandoArchivo}
                  onDescargar={descargarArchivo}
                  onReejecutar={reejecutarProceso}
                />
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
